import * as React from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';

export interface InfoHintFooterAction {
  label: string;
  onClick: () => void;
}

export interface InfoHintProps {
  label: string;
  children: React.ReactNode;
  footerAction?: InfoHintFooterAction;
  side?: React.ComponentPropsWithoutRef<typeof PopoverContent>['side'];
  align?: React.ComponentPropsWithoutRef<typeof PopoverContent>['align'];
  iconClassName?: string;
  className?: string;
  contentClassName?: string;
}

export function InfoHint({
  label,
  children,
  footerAction,
  side = 'top',
  align = 'center',
  iconClassName,
  className,
  contentClassName,
}: InfoHintProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef<number | null>(null);

  const clearCloseTimeout = (): void => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openHint = (): void => {
    clearCloseTimeout();
    setOpen(true);
  };

  const scheduleClose = (): void => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 200);
  };

  React.useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  const handleOpenChange = (nextOpen: boolean): void => {
    clearCloseTimeout();
    setOpen(nextOpen);
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    clearCloseTimeout();
    setOpen((currentlyOpen) => !currentlyOpen);
  };

  const handleFooterClick = (): void => {
    clearCloseTimeout();
    setOpen(false);
    footerAction?.onClick();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'inline-flex shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
          onMouseEnter={openHint}
          onMouseLeave={scheduleClose}
          onClick={handleTriggerClick}
        >
          <Info className={cn('h-4 w-4 cursor-pointer opacity-70 hover:opacity-100', iconClassName)} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className={cn(
          'z-[2010] max-w-xs border border-border bg-background p-3 text-sm text-foreground shadow-lg',
          contentClassName,
        )}
        onMouseEnter={openHint}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-2">
          <div>{children}</div>
          {footerAction && (
            <div className="border-t border-border pt-2">
              <button
                type="button"
                onClick={handleFooterClick}
                className="inline-flex items-center gap-1 text-left text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80"
              >
                {footerAction.label}
                <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
